import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, FormHelperText } from '@mui/material';
import CreateDailyPractice from './DailyPracticeModal';
import '../labPractice/LabPractice.css';
import DailyPracticeTable from './DailyPracticeTable';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { toast } from 'react-toastify';
import { disableDailyPractice, getAllCoeLabs, getAllDailyPractice, getDailyPracticeQR } from '../../../services/mentorServices/DailyPracticeServices';
import ConfirmDisableModal from '../../ConfirmDisableModal';

const DailyPractice = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selection, setSelection] = useState({ lab: '', coe: '' });
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showTable, setShowTable] = useState(false);
    const [error, setError] = useState({ lab: '', coe: '' });
    const [coes, setCoe] = useState([]);
    const [labNames, setLabNames] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [disableModalOpen, setDisableModalOpen] = useState(false);
    const [selectedPractice, setSelectedPractice] = useState(null);

    const handleModalToggle = () => setModalOpen(prev => !prev);

    const handleSelectionChange = (event) => {
        const { name, value } = event.target;
        setSelection(prev => {
            if (name === 'coe') {
                fetchLabs(value);
                return { coe: value, lab: '' };
            }
            return { ...prev, [name]: value };
        });
        setError(prev => ({ ...prev, [name]: '' }));
    };

    const fetchData = useCallback(async () => {
        const { lab, coe } = selection;
        if (!lab || !coe) {
            setError({
                lab: lab ? '' : 'Please select a Lab',
                coe: coe ? '' : 'Please select a COE',
            });
            return;
        }
        setLoading(true);
        try {
            const response = await getAllDailyPractice(coe, lab);
            setTableData(response.data.data);
            setShowTable(true);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [selection]);

    const fetchLabs = useCallback(async (coeId) => {
        try {
            const response = await getAllCoeLabs(coeId);
            setLabNames(response?.data.data);
        } catch (error) {
            setLabNames([]);
            setTableData([]);
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    }, []);

    const getCoe = async () => {
        try {
            const response = await getAllCOE();
            setCoe(response.data.data);
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    };

    const disablePractice = async () => {
        if (!selectedPractice) return;
        try {
            const response = await disableDailyPractice(
                selectedPractice.daily_practice_id,
                selection.coe,
                selection.lab,
                selectedPractice.MakerPlan?.makerplanID,
                selectedPractice.Maker_Module?.moduleID,
                selectedPractice.Topic?.topicID
            );
            toast.success(response?.data.message);
            fetchData();
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        } finally {
            setDisableModalOpen(false);
            setSelectedPractice(null);
        }
    };

    const downloadQR = async (row) => {
        try {
            const response = await getDailyPracticeQR(row.daily_practice_id);
            if (response && response.data) {
                const blob = new Blob([response.data], { type: 'image/png' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `dailyPractice-${row?.MakerPlan?.makerplan_Name?.trim()}-${row?.Maker_Module?.module_Name?.trim()}-${row?.Topic?.topic_Name?.trim()}-${row?.daily_practice_name?.trim()}.png`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                throw new Error('Invalid response data');
            }
        } catch (error) {
            toast.error(error?.response?.data.message || error?.message || error?.error);
        }
    };

    useEffect(() => {
        getCoe();
    }, []);

    const isActionDisabled = !selection.coe || !selection.lab || labNames.length === 0;

    return (
        <Box className="dashboard-container">
            <Box className="DailyPractice-header">
                <Box className="daily-practice-inputs" display="flex" sx={{ width: "60% !important" }} alignItems="center">
                    <FormControl size="small" className="lab-name-select" sx={{ width: "43% !important" }} error={!!error.coe}>
                        <InputLabel id="coe-select-label">COE*</InputLabel>
                        <Select
                            labelId="coe-select-label"
                            id="coe-select"
                            name="coe"
                            value={selection.coe}
                            onChange={handleSelectionChange}
                            label="COE"
                        >
                            {coes.map((coe) => (
                                <MenuItem key={coe.id} value={coe.id}>
                                    {coe.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.coe && <FormHelperText>{error.coe}</FormHelperText>}
                    </FormControl>
                    <FormControl size="small" className="lab-name-select" sx={{ width: "43% !important" }} error={!!error.lab}>
                        <InputLabel id="lab-select-label">Lab Name*</InputLabel>
                        <Select
                            labelId="lab-select-label"
                            id="lab-select"
                            name="lab"
                            value={selection.lab}
                            onChange={handleSelectionChange}
                            label="Lab Name"
                        >
                            {labNames.map((lab) => (
                                <MenuItem key={lab.id} value={lab.id}>
                                    {lab.lab_name}
                                </MenuItem>
                            ))}
                        </Select>
                        {error.lab && <FormHelperText>{error.lab}</FormHelperText>}
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchData}
                        className="dashboard-header-button"
                        disabled={loading || isActionDisabled}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'VIEW'}
                    </Button>
                </Box>
                {showTable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleModalToggle}
                        className="dashboard-header-button"
                        disabled={isActionDisabled}
                    >
                        CREATE DAILY PRACTICE
                    </Button>
                )}
                <CreateDailyPractice
                    open={modalOpen}
                    onClose={handleModalToggle}
                    selection={selection}
                    selectedRow={selectedRow}
                    fetchData={fetchData}
                    setSelectedRow={setSelectedRow}
                />
            </Box>
            <Box className="dashboard-box-container">
                {showTable && (
                    <DailyPracticeTable
                        data={tableData}
                        handleModalToggle={handleModalToggle}
                        setSelectedRow={setSelectedRow}
                        disablePractice={disablePractice}
                        downloadQR={downloadQR}
                        selectedPractice={selectedPractice}
                        setSelectedPractice={setSelectedPractice}
                        setDisableModalOpen={setDisableModalOpen}
                        modalOpen={modalOpen}
                        loading={loading}
                        setLoading={setLoading}
                    />
                )}
            </Box>
            <ConfirmDisableModal
                open={disableModalOpen}
                onClose={() => setDisableModalOpen(false)}
                onConfirm={disablePractice}
                message="Daily Practice Record"
            />
        </Box>
    );
};

export default DailyPractice;
